/**
 * @fileoverview Conflict Resolution Dialog Component
 * 
 * Provides UI for resolving assignment conflicts with different resolution strategies.
 * Shows conflicts categorized by resolution type and provides appropriate resolution options.
 * 
 * Requirements: 4.4, 4.5
 */

import React, { useState, useCallback, useEffect } from 'react';

/**
 * Conflict Resolution Dialog Component
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether dialog is open
 * @param {AssignmentConflict[]} props.conflicts - Conflicts to resolve
 * @param {Array} props.files - Available files for name lookup
 * @param {Array} props.parrots - Available parrots for name lookup
 * @param {function(Object): void} props.onResolve - Callback when conflicts are resolved
 * @param {function(): void} props.onCancel - Callback when dialog is cancelled
 * @param {boolean} [props.loading=false] - Whether resolution is in progress
 * @param {string} [props.className] - CSS class name
 */
export function ConflictResolutionDialog({ 
  isOpen, 
  conflicts = [], 
  files = [], 
  parrots = [], 
  onResolve, 
  onCancel, 
  loading = false,
  className=''
}) {
  const [resolutions, setResolutions] = useState({});

  // Initialize resolutions with suggested values
  useEffect(() => {
    const initialResolutions = {};
    conflicts.forEach(conflict => {
      if (conflict.suggestedResolution) {
        initialResolutions[conflict.id] = conflict.suggestedResolution;
      }
    });
    setResolutions(initialResolutions);
  }, [conflicts]);

  // Handle resolution change for a conflict
  const handleResolutionChange = useCallback((conflictId, resolution) => {
    setResolutions(prev => ({
      ...prev,
      [conflictId]: resolution
    }));
  }, []);

  // Handle resolve all conflicts
  const handleResolve = useCallback(async () => {
    if (onResolve) {
      await onResolve(resolutions);
    }
  }, [resolutions, onResolve]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && onCancel) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onCancel]);

  // Get file name by ID
  const getFileName = useCallback((fileId) => {
    const file = files.find(f => f.id === fileId);
    return file?.name || fileId;
  }, [files]);

  // Get parrot name by ID
  const getParrotName = useCallback((parrotId) => {
    const parrot = parrots.find(p => p.id === parrotId);
    return parrot?.name || parrotId;
  }, [parrots]);

  // Categorize conflicts by resolution strategy
  const categorizedConflicts = useCallback(() => {
    const categories = {
      user_choice: [],
      admin_required: [],
      auto_resolve: []
    };

    conflicts.forEach(conflict => {
      const strategy = conflict.resolutionStrategy || 'user_choice';
      if (categories[strategy]) {
        categories[strategy].push(conflict);
      } else {
        categories.user_choice.push(conflict);
      }
    });

    return categories;
  }, [conflicts]);

  // Check if all user choice conflicts have resolutions
  const canResolve = useCallback(() => {
    const categories = categorizedConflicts();
    const userChoiceConflicts = categories.user_choice;
    
    // Can't resolve if there are admin required conflicts
    if (categories.admin_required.length > 0) {
      return false;
    }

    // All user choice conflicts must have resolutions
    return userChoiceConflicts.every(conflict => resolutions[conflict.id]);
  }, [categorizedConflicts, resolutions]);

  if (!isOpen) return null;

  const categories = categorizedConflicts();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
          onClick={onCancel}
        />

        {/* Dialog */}
        <div className={`inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-2xl ${className}`}>
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/30">
                <span className="text-2xl">⚠️</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Resolve Assignment Conflicts
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
                  {conflicts.length} conflict{conflicts.length !== 1 ? 's need' : ' needs'} to be resolved before proceeding
                </p>
              </div>
            </div>
            
            <button
              onClick={onCancel}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors dark:text-gray-300 dark:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Loading state */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" role="status"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">Resolving conflicts...</span>
            </div>
          )}

          {/* Content */}
          {!loading && (
            <>
              {/* User Decisions Required */}
              {categories.user_choice.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                    User Decisions Required ({categories.user_choice.length})
                  </h4>
                  <div className="space-y-4">
                    {categories.user_choice.map((conflict) => (
                      <ConflictResolutionItem
                        key={conflict.id}
                        conflict={conflict}
                        files={files}
                        parrots={parrots}
                        resolution={resolutions[conflict.id]}
                        onResolutionChange={(resolution) => handleResolutionChange(conflict.id, resolution)}
                        getFileName={getFileName}
                        getParrotName={getParrotName}
                        disabled={loading}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Administrator Action Required */}
              {categories.admin_required.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                    Administrator Action Required ({categories.admin_required.length})
                  </h4>
                  <div className="space-y-4">
                    {categories.admin_required.map((conflict) => (
                      <div key={conflict.id} className="p-4 border border-red-200 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-900/20">
                        <div className="flex items-start space-x-3">
                          <span className="text-2xl">🚫</span>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="font-medium text-gray-900 dark:text-white">
                                {getFileName(conflict.fileId)} → {getParrotName(conflict.parrotId)}
                              </span>
                              <span className="px-2 py-1 text-xs rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                                {conflict.type?.replace('_', ' ') || 'Unknown'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300 mb-2 dark:text-gray-600">
                              {conflict.message}
                            </p>
                            {conflict.details && (
                              <div className="text-xs text-gray-600 dark:text-gray-400 mb-2 dark:text-gray-300 dark:text-gray-600">
                                {Object.entries(conflict.details).map(([key, value]) => (
                                  <div key={key}>
                                    <span className="capitalize">{key.replace(/([A-Z])/g, ' $1').toLowerCase()}: </span>
                                    <span>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="text-sm font-medium text-red-700 dark:text-red-300">
                              Administrator action required
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Auto-Resolved */}
              {categories.auto_resolve.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                    Auto-Resolved ({categories.auto_resolve.length})
                  </h4>
                  <div className="space-y-4">
                    {categories.auto_resolve.map((conflict) => (
                      <div key={conflict.id} className="p-4 border border-green-200 dark:border-green-800 rounded-lg bg-green-50 dark:bg-green-900/20">
                        <div className="flex items-start space-x-3">
                          <span className="text-2xl">🔒</span>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="font-medium text-gray-900 dark:text-white">
                                {getFileName(conflict.fileId)} → {getParrotName(conflict.parrotId)}
                              </span>
                              <span className="px-2 py-1 text-xs rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300">
                                {conflict.type?.replace('_', ' ') || 'Unknown'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300 mb-2 dark:text-gray-600">
                              {conflict.message}
                            </p>
                            <div className="text-sm font-medium text-green-700 dark:text-green-300">
                              Will be resolved automatically
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={onCancel}
                  disabled={loading}
                  className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors dark:bg-gray-800 dark:bg-gray-900 dark:text-gray-600"
                >
                  Cancel
                </button>
                
                <button
                  onClick={handleResolve}
                  disabled={!canResolve() || loading}
                  className="px-4 py-2 text-sm text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:text-white"
                >
                  {loading ? 'Resolving...' : 'Resolve Conflicts'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Individual Conflict Resolution Item
 */
function ConflictResolutionItem({ 
  conflict, 
  resolution, 
  onResolutionChange, 
  getFileName, 
  getParrotName, 
  disabled = false 
}) {
  const getConflictIcon = (type) => {
    switch (type) {
      case 'duplicate_assignment': return '⚠️';
      case 'resource_limit': return '🚫';
      case 'permission_denied': return '🔒';
      case 'sync_conflict': return '⚡';
      default: return '❓';
    }
  };

  const getConflictBorderColor = (type) => {
    switch (type) {
      case 'duplicate_assignment': return 'border-yellow-200 dark:border-yellow-800';
      case 'resource_limit': return 'border-red-200 dark:border-red-800';
      case 'permission_denied': return 'border-orange-200 dark:border-orange-800';
      case 'sync_conflict': return 'border-blue-200 dark:border-blue-800';
      default: return 'border-gray-200 dark:border-gray-700';
    }
  };

  const getConflictBgColor = (type) => {
    switch (type) {
      case 'duplicate_assignment': return 'bg-yellow-50 dark:bg-yellow-900/20';
      case 'resource_limit': return 'bg-red-50 dark:bg-red-900/20';
      case 'permission_denied': return 'bg-orange-50 dark:bg-orange-900/20';
      case 'sync_conflict': return 'bg-blue-50 dark:bg-blue-900/20';
      default: return 'bg-gray-50 dark:bg-gray-800';
    }
  };

  return (
    <div className={`border rounded-lg p-4 ${getConflictBorderColor(conflict.type)} ${getConflictBgColor(conflict.type)}`}>
      {/* Conflict header */}
      <div className="flex items-start space-x-3 mb-3">
        <div className="flex-shrink-0">
          <span className="text-2xl">{getConflictIcon(conflict.type)}</span>
        </div>
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <span className="font-medium text-gray-900 dark:text-white">
              {getFileName(conflict.fileId)} → {getParrotName(conflict.parrotId)}
            </span>
            <span className="px-2 py-1 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 dark:text-gray-600">
              {conflict.type?.replace('_', ' ') || 'Unknown'}
            </span>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-2 dark:text-gray-600">
            {conflict.message}
          </p>
          {conflict.details && (
            <div className="text-xs text-gray-600 dark:text-gray-400 mb-3 dark:text-gray-300 dark:text-gray-600">
              {Object.entries(conflict.details).map(([key, value]) => (
                <div key={key}>
                  {key === 'currentCount' && conflict.details.maxCount && (
                    <span>Current: {value}, Maximum: {conflict.details.maxCount}</span>
                  )}
                  {key === 'requiredPermission' && (
                    <span>Required permission: {value}</span>
                  )}
                  {key === 'existingAssignmentId' && (
                    <span>Existing assignment: {value}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Resolution options */}
      <div className="ml-11">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
          Choose Resolution:
        </h4>
        <div className="space-y-2" role="radiogroup" aria-labelledby={`conflict-${conflict.id}-label`}>
          {conflict.resolutionOptions?.map(option => (
            <label key={option} className="flex items-start space-x-2 cursor-pointer">
              <input
                type="radio"
                name={`conflict-${conflict.id}`}
                value={option}
                checked={resolution === option}
                onChange={(e) => onResolutionChange(e.target.value)}
                disabled={disabled}
                className="mt-0.5 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:bg-gray-800"
                aria-label={getResolutionLabel(option, conflict.type)}
              />
              <div className="flex-1">
                <span className="text-sm text-gray-700 dark:text-gray-300 font-medium dark:text-gray-600">
                  {getResolutionLabel(option, conflict.type)}
                </span>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 dark:text-gray-300 dark:text-gray-600">
                  {getResolutionDescription(option, conflict.type)}
                </div>
              </div>
            </label>
          ))}
        </div>

        {/* Suggested resolution */}
        {conflict.suggestedResolution && (
          <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
            <p className="text-xs text-blue-700 dark:text-blue-300">
              <strong>Suggested:</strong> {getResolutionLabel(conflict.suggestedResolution, conflict.type)}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Get human-readable label for resolution action
 */
function getResolutionLabel(action, conflictType) {
  const labels = {
    duplicate_assignment: {
      skip: 'Skip this assignment',
      overwrite: 'Overwrite existing assignment',
      merge: 'Merge with existing assignment'
    },
    resource_limit: {
      skip: 'Skip this assignment',
      increase_limit: 'Request limit increase',
      remove_oldest: 'Remove oldest assignment'
    },
    permission_denied: {
      skip: 'Skip this assignment',
      request_permission: 'Request permission from admin'
    },
    sync_conflict: {
      skip: 'Skip this assignment',
      force_sync: 'Force sync (may overwrite remote changes)',
      manual_resolve: 'Mark for manual resolution'
    }
  };

  return labels[conflictType]?.[action] || action;
}

/**
 * Get description for resolution action
 */
function getResolutionDescription(action, conflictType) {
  const descriptions = {
    duplicate_assignment: {
      skip: 'Do not create this assignment',
      overwrite: 'Replace the existing assignment',
      merge: 'Combine with existing assignment'
    },
    resource_limit: {
      skip: 'Do not create this assignment',
      increase_limit: 'Request administrator to increase limit',
      remove_oldest: 'Remove the oldest assignment to make room'
    },
    permission_denied: {
      skip: 'Do not create this assignment',
      request_permission: 'Request administrator permission'
    },
    sync_conflict: {
      skip: 'Do not create this assignment',
      force_sync: 'Override remote changes',
      manual_resolve: 'Require manual intervention'
    }
  };

  return descriptions[conflictType]?.[action] || '';
}

export default ConflictResolutionDialog;
